import React, { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { CheckCircle2, Clock, Upload, ShieldOff, RotateCcw, Download, AlertTriangle, X, Plus, EyeOff, Eye, Trash2, Package, BookMarked } from "lucide-react";
import api, { formatApiError, API_BASE } from "../../lib/api";
import { formatDateTime } from "../../lib/datetime";
import { Button, Pill } from "../common/Primitives";
import ConfirmReasonDialog from "../common/ConfirmReasonDialog";

/**
 * ClosingChecklistWidget
 * Right-rail card that appears on the deal-room detail page when the room is
 * at 'Definitive Docs' or 'Closed'. Codifies the last-5% work so nothing
 * stalls on process ambiguity.
 */
export default function ClosingChecklistWidget({ roomId, viewerRole, isAdmin, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [requestingKey, setRequestingKey] = useState(null); // item_key being submitted
  const [exceptionTarget, setExceptionTarget] = useState(null); // {key, label}
  const [reopenTarget, setReopenTarget] = useState(null); // {key, label}
  const [addingItem, setAddingItem] = useState(false);
  const [newItem, setNewItem] = useState({ label: "", hint: "", requires_upload: true });
  const [savingItem, setSavingItem] = useState(false);
  const [hiddenPanelOpen, setHiddenPanelOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [templatesLoaded, setTemplatesLoaded] = useState(false);
  const [templatePickerOpen, setTemplatePickerOpen] = useState(false);
  const [templateNameForSave, setTemplateNameForSave] = useState("");
  const [saveTplOpen, setSaveTplOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get(`/deal-room/${roomId}/closing-checklist`);
      setData(r.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, [roomId]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="border border-navy/10 bg-white/60 backdrop-blur p-4 rounded-md" data-testid="closing-checklist-loading">
        <div className="overline mb-2">Closing Checklist</div>
        <div className="text-xs text-ink-muted italic">Loading…</div>
      </div>
    );
  }
  if (!data || !data.eligible) return null; // hidden until Definitive Docs

  const submitApprove = async (itemKey, note) => {
    try {
      await api.post(`/deal-room/${roomId}/closing-checklist/${itemKey}/complete`, { note });
      toast.success("Item approved and marked complete.");
      await load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitException = async ({ reason }) => {
    if (!exceptionTarget) return;
    try {
      await api.post(`/deal-room/${roomId}/closing-checklist/${exceptionTarget.key}/exception`, { reason });
      toast.success(`Exception granted for '${exceptionTarget.label}'.`);
      setExceptionTarget(null);
      await load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const submitReopen = async ({ reason }) => {
    if (!reopenTarget) return;
    try {
      await api.post(`/deal-room/${roomId}/closing-checklist/${reopenTarget.key}/reopen`, { reason });
      toast.success(`Re-opened '${reopenTarget.label}'.`);
      setReopenTarget(null);
      await load();
      onChanged?.();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ------------ CMS: add / remove / hide / unhide ------------
  const saveNewItem = async () => {
    if (!newItem.label.trim() || newItem.label.trim().length < 3) {
      toast.error("Label must be at least 3 characters.");
      return;
    }
    setSavingItem(true);
    try {
      await api.post(`/deal-room/${roomId}/closing-checklist/items`, {
        label: newItem.label.trim(),
        hint: newItem.hint.trim() || null,
        requires_upload: newItem.requires_upload,
      });
      toast.success("Custom item added.");
      setNewItem({ label: "", hint: "", requires_upload: true });
      setAddingItem(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSavingItem(false); }
  };

  const removeItem = async (item) => {
    if (!window.confirm(`Remove '${item.label}' from this deal's checklist? This is permanent for the custom item.`)) return;
    try {
      await api.delete(`/deal-room/${roomId}/closing-checklist/items/${item.key}`);
      toast.success("Item removed.");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const hideItem = async (item) => {
    try {
      await api.post(`/deal-room/${roomId}/closing-checklist/items/${item.key}/hide`, { reason: null });
      toast.success(`Hid '${item.label}' from this deal.`);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const unhideItem = async (key) => {
    try {
      await api.post(`/deal-room/${roomId}/closing-checklist/items/${key}/unhide`, {});
      toast.success("Item restored.");
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ----- Templates -----
  const loadTemplates = async () => {
    try {
      const r = await api.get("/admin/closing-checklist-templates");
      setTemplates(r.data?.items || []);
      setTemplatesLoaded(true);
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const applyTemplate = async (template_id, replace) => {
    if (!template_id) return;
    try {
      await api.post(`/deal-room/${roomId}/closing-checklist/apply-template`, { template_id, replace });
      toast.success("Template applied.");
      setTemplatePickerOpen(false);
      await load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const saveAsTemplate = async () => {
    const name = templateNameForSave.trim();
    if (name.length < 3) { toast.error("Template name must be at least 3 characters."); return; }
    try {
      await api.post("/admin/closing-checklist-templates", { name, source_room_id: roomId });
      toast.success(`Saved as "${name}"`);
      setSaveTplOpen(false); setTemplateNameForSave("");
      setTemplatesLoaded(false); // force refetch on next open
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const deleteTemplate = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? This can't be undone.`)) return;
    try {
      await api.delete(`/admin/closing-checklist-templates/${t.template_id}`);
      toast.success("Template deleted.");
      setTemplates((arr) => arr.filter((x) => x.template_id !== t.template_id));
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // ----- Closing pack ZIP -----
  const downloadClosingPack = async () => {
    try {
      const r = await api.get(`/deal-room/${roomId}/closing-pack.zip`, { responseType: "blob" });
      const blob = new Blob([r.data], { type: "application/zip" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const disp = r.headers?.["content-disposition"] || "";
      const m = disp.match(/filename="([^"]+)"/);
      a.download = m?.[1] || `closing-pack__${roomId}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
      toast.success("Closing pack downloaded.");
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="border border-navy/10 bg-white/60 backdrop-blur p-4 rounded-md" data-testid="closing-checklist">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="overline">Closing Checklist</div>
          <div className="text-[10px] font-mono text-ink-muted mt-0.5">
            Last-mile items to finish this raise. Upload proof — admin approves.
          </div>
        </div>
        <div className="text-right">
          <div className="font-heading text-ink text-xl">{data.complete}/{data.total}</div>
          <div className="text-[10px] font-mono text-ink-muted">{data.percent}% complete</div>
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1.5 rounded bg-navy/10 overflow-hidden mb-3" data-testid="closing-progress">
        <div
          className={`h-full ${data.ready_to_close ? "bg-emerald-500" : "bg-gold"} transition-all`}
          style={{ width: `${data.percent}%` }}
        />
      </div>

      {data.ready_to_close && isAdmin && (
        <div className="text-[11px] font-mono uppercase tracking-widest bg-emerald-500/10 text-emerald-800 rounded px-2 py-1 mb-3 flex items-center gap-1" data-testid="closing-ready-banner">
          <CheckCircle2 size={12} /> All items done — hit &quot;Close deal&quot; to issue the success-fee invoice
        </div>
      )}

      {/* Admin CMS bar — add custom item + show hidden items */}
      {isAdmin && (
        <div className="mb-3 flex items-center gap-2 flex-wrap" data-testid="closing-cms-bar">
          {!addingItem && (
            <button
              onClick={() => setAddingItem(true)}
              className="text-[11px] font-heading text-navy hover:bg-navy/10 rounded px-2 py-1 border border-navy/30 inline-flex items-center gap-1"
              data-testid="closing-add-item-open"
            >
              <Plus size={11} /> Add custom item
            </button>
          )}
          <button
            onClick={() => { setTemplatePickerOpen((v) => !v); if (!templatesLoaded) loadTemplates(); }}
            className="text-[11px] font-heading text-navy hover:bg-navy/10 rounded px-2 py-1 border border-navy/30 inline-flex items-center gap-1"
            data-testid="closing-templates-toggle"
          >
            <BookMarked size={11} /> Templates
          </button>
          <button
            onClick={() => setSaveTplOpen(true)}
            disabled={!((data.items || []).some((it) => it.is_custom) || (data.hidden_default_keys || []).length > 0)}
            title={
              !((data.items || []).some((it) => it.is_custom) || (data.hidden_default_keys || []).length > 0)
                ? "Add a custom item or hide a default first"
                : "Save this checklist as a reusable template"
            }
            className="text-[11px] font-heading text-ink-muted hover:text-ink rounded px-2 py-1 border border-navy/15 inline-flex items-center gap-1 disabled:opacity-40 disabled:cursor-not-allowed"
            data-testid="closing-templates-save"
          >
            Save as template
          </button>
          {data.hidden_default_keys?.length > 0 && (
            <button
              onClick={() => setHiddenPanelOpen((v) => !v)}
              className="text-[11px] font-heading text-ink-muted hover:text-ink rounded px-2 py-1 border border-navy/15 inline-flex items-center gap-1"
              data-testid="closing-toggle-hidden"
            >
              <Eye size={11} /> {hiddenPanelOpen ? "Hide" : "Show"} hidden ({data.hidden_default_keys.length})
            </button>
          )}
        </div>
      )}

      {/* Closing pack ZIP — visible to admin always (partial) and to parties when ready */}
      {(isAdmin || data.ready_to_close) && data.total > 0 && (
        <div className="mb-3">
          <button
            onClick={downloadClosingPack}
            className={`w-full text-xs font-heading rounded px-3 py-2 inline-flex items-center justify-center gap-2 border transition ${
              data.ready_to_close
                ? "bg-emerald-500/10 text-emerald-800 border-emerald-500/40 hover:bg-emerald-500/20"
                : "bg-navy/5 text-ink-soft border-navy/15 hover:bg-navy/10"
            }`}
            data-testid="closing-pack-download"
          >
            <Package size={13} />
            {data.ready_to_close
              ? "Download closing pack (ZIP)"
              : `Download partial pack (${data.complete}/${data.total})`}
          </button>
          {!data.ready_to_close && isAdmin && (
            <div className="text-[10px] font-mono text-ink-muted mt-1 text-center">
              Admin-only until all items complete. Includes evidence + signed PDFs + manifest.
            </div>
          )}
        </div>
      )}

      {/* Templates picker */}
      {isAdmin && templatePickerOpen && (
        <div className="mb-3 border border-navy/15 rounded p-2 bg-white/40" data-testid="closing-templates-panel">
          <div className="flex items-center justify-between mb-1">
            <div className="overline">Load a template</div>
            <button onClick={() => setTemplatePickerOpen(false)}><X size={12} className="text-ink-muted" /></button>
          </div>
          {!templatesLoaded ? (
            <div className="text-xs text-ink-muted italic">Loading…</div>
          ) : templates.length === 0 ? (
            <div className="text-xs text-ink-muted italic">
              No templates saved yet. Configure this deal&apos;s checklist, then click &quot;Save as template&quot;.
            </div>
          ) : (
            <div className="divide-y divide-navy/[0.06]">
              {templates.map((t) => (
                <div key={t.template_id} className="py-1.5 flex items-center justify-between gap-2" data-testid={`closing-template-${t.template_id}`}>
                  <div className="min-w-0 flex-1">
                    <div className="font-heading text-ink text-xs">{t.name}</div>
                    <div className="text-[10px] font-mono text-ink-muted">
                      {(t.custom_items || []).length} items · {(t.hidden_default_keys || []).length} hidden · used {t.usage_count || 0}×
                    </div>
                  </div>
                  <div className="flex items-center gap-1 shrink-0">
                    <button
                      onClick={() => applyTemplate(t.template_id, false)}
                      className="text-[10px] font-heading text-navy hover:bg-navy/10 rounded px-1.5 py-0.5 border border-navy/30"
                      data-testid={`closing-template-append-${t.template_id}`}
                    >Append</button>
                    <button
                      onClick={() => { if (window.confirm(`Replace this deal's custom items with template "${t.name}"?`)) applyTemplate(t.template_id, true); }}
                      className="text-[10px] font-heading text-[#8A6600] hover:bg-gold/10 rounded px-1.5 py-0.5 border border-gold/40"
                      data-testid={`closing-template-replace-${t.template_id}`}
                    >Replace</button>
                    <button
                      onClick={() => deleteTemplate(t)}
                      className="text-rose-700 hover:bg-rose-500/10 rounded p-0.5"
                      title="Delete template"
                      data-testid={`closing-template-delete-${t.template_id}`}
                    ><Trash2 size={10} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Save-as-template */}
      {isAdmin && saveTplOpen && (
        <div className="mb-3 border border-navy/15 rounded p-2 bg-white/40 space-y-2" data-testid="closing-save-template-form">
          <div className="flex items-center justify-between">
            <div className="overline">Save current checklist as template</div>
            <button onClick={() => { setSaveTplOpen(false); setTemplateNameForSave(""); }}>
              <X size={12} className="text-ink-muted" />
            </button>
          </div>
          <input
            type="text"
            value={templateNameForSave}
            onChange={(e) => setTemplateNameForSave(e.target.value)}
            placeholder="Template name (e.g. SaaS Series-A · India)"
            className="w-full text-xs border border-navy/15 rounded px-2 py-1"
            data-testid="closing-save-template-name"
          />
          <div className="text-[10px] font-mono text-ink-muted">
            Snapshots this deal&apos;s custom items + hidden defaults. Per-item request/upload state is NOT included.
          </div>
          <Button variant="gold" onClick={saveAsTemplate} testId="closing-save-template-submit">
            Save template
          </Button>
        </div>
      )}

      {isAdmin && addingItem && (
        <div className="mb-3 border border-navy/15 rounded p-3 bg-white/40 space-y-2" data-testid="closing-add-item-form">
          <div className="flex items-center justify-between">
            <div className="overline">New checklist item</div>
            <button onClick={() => { setAddingItem(false); setNewItem({ label: "", hint: "", requires_upload: true }); }}>
              <X size={14} className="text-ink-muted hover:text-ink" />
            </button>
          </div>
          <input
            type="text"
            value={newItem.label}
            onChange={(e) => setNewItem((v) => ({ ...v, label: e.target.value }))}
            placeholder="Item label (e.g. Founder earn-out schedule)"
            className="w-full text-xs border border-navy/15 rounded px-2 py-1"
            data-testid="closing-new-item-label"
          />
          <input
            type="text"
            value={newItem.hint}
            onChange={(e) => setNewItem((v) => ({ ...v, hint: e.target.value }))}
            placeholder="Hint / instruction shown to founder & investor (optional)"
            className="w-full text-xs border border-navy/15 rounded px-2 py-1"
            data-testid="closing-new-item-hint"
          />
          <label className="flex items-center gap-2 text-xs text-ink-soft cursor-pointer">
            <input
              type="checkbox"
              checked={newItem.requires_upload}
              onChange={(e) => setNewItem((v) => ({ ...v, requires_upload: e.target.checked }))}
              data-testid="closing-new-item-requires-upload"
            />
            Requires proof file upload
          </label>
          <div className="flex gap-2">
            <Button variant="gold" onClick={saveNewItem} disabled={savingItem} testId="closing-new-item-save">
              {savingItem ? "Adding…" : "Add item"}
            </Button>
            <button onClick={() => { setAddingItem(false); setNewItem({ label: "", hint: "", requires_upload: true }); }}
              className="text-[11px] text-ink-muted hover:text-ink">
              Cancel
            </button>
          </div>
        </div>
      )}

      {isAdmin && hiddenPanelOpen && data.hidden_default_keys?.length > 0 && (
        <div className="mb-3 border border-dashed border-navy/15 rounded p-2 bg-navy/[0.02] space-y-1" data-testid="closing-hidden-panel">
          <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Hidden on this deal</div>
          {data.hidden_default_keys.map((k) => (
            <div key={k} className="flex items-center justify-between text-xs" data-testid={`closing-hidden-${k}`}>
              <span className="font-mono text-ink-muted">{k}</span>
              <button
                onClick={() => unhideItem(k)}
                className="text-[11px] text-navy hover:underline inline-flex items-center gap-0.5"
                data-testid={`closing-unhide-${k}`}
              >
                <Eye size={11} /> Restore
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="space-y-2">
        {data.items.map((it) => (
          <ChecklistRow
            key={it.key}
            item={it}
            roomId={roomId}
            isAdmin={isAdmin}
            viewerRole={viewerRole}
            requesting={requestingKey === it.key}
            setRequesting={setRequestingKey}
            onSubmitted={load}
            onApprove={(note) => submitApprove(it.key, note)}
            onException={() => setExceptionTarget({ key: it.key, label: it.label })}
            onReopen={() => setReopenTarget({ key: it.key, label: it.label })}
            onHide={() => hideItem(it)}
            onRemove={() => removeItem(it)}
          />
        ))}
      </div>

      <ConfirmReasonDialog
        open={!!exceptionTarget}
        onClose={() => setExceptionTarget(null)}
        onConfirm={submitException}
        title={`Grant exception for '${exceptionTarget?.label || ""}'?`}
        description="Skip the required proof upload for this item. Reason will be recorded in the audit trail and shown to both parties."
        confirmLabel="Grant exception"
        reasonLabel="Reason (required — visible to both parties)"
        reasonRequired
        tone="gold"
        testId="closing-exception-dialog"
      />

      <ConfirmReasonDialog
        open={!!reopenTarget}
        onClose={() => setReopenTarget(null)}
        onConfirm={submitReopen}
        title={`Re-open '${reopenTarget?.label || ""}'?`}
        description="This flips the item back to pending. Requester will need to submit fresh proof."
        confirmLabel="Re-open item"
        reasonLabel="Why are you re-opening this?"
        reasonRequired
        tone="danger"
        testId="closing-reopen-dialog"
      />
    </div>
  );
}


// ---------------------------------------------------------------------------
// One row per checklist item — handles the local request/upload form too.
// ---------------------------------------------------------------------------
function ChecklistRow({ item, roomId, isAdmin, requesting, setRequesting, onSubmitted, onApprove, onException, onReopen, onHide, onRemove }) {
  const [note, setNote] = useState("");
  const [approveNote, setApproveNote] = useState("");
  const [approving, setApproving] = useState(false);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileInputRef = useRef(null);

  const isComplete = item.status === "complete";
  const isException = item.status === "exception";
  const isRequested = item.status === "requested";
  const isAuto = !!item.auto_source;
  const canRequest = !isComplete && !isException && !isRequested && !isAuto;

  const submitRequest = async () => {
    if (item.requires_upload && !file) {
      toast.error(`'${item.label}' requires a proof file. Attach one or ask an admin for an exception.`);
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("note", note);
      if (file) fd.append("file", file);
      await api.post(`/deal-room/${roomId}/closing-checklist/${item.key}/request`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success("Request submitted — an admin will review it shortly.");
      setNote(""); setFile(null); setRequesting(null);
      onSubmitted?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const submitApprove = async () => {
    setApproving(true);
    try { await onApprove(approveNote); setApproveNote(""); }
    finally { setApproving(false); }
  };

  const statusPill = () => {
    if (isComplete) return <Pill tone="success" data-testid={`closing-status-${item.key}`}><CheckCircle2 size={10} className="inline mr-1" /> Complete{isAuto ? " · auto" : ""}</Pill>;
    if (isException) return <Pill tone="warn" data-testid={`closing-status-${item.key}`}><ShieldOff size={10} className="inline mr-1" /> Exception</Pill>;
    if (isRequested) return <Pill tone="gold" data-testid={`closing-status-${item.key}`}><Clock size={10} className="inline mr-1" /> Awaiting admin</Pill>;
    if (isAuto) return <Pill tone="muted" data-testid={`closing-status-${item.key}`}>Auto — pending signal</Pill>;
    return <Pill tone="muted" data-testid={`closing-status-${item.key}`}>Pending</Pill>;
  };

  return (
    <div className="border border-navy/[0.08] rounded p-2.5" data-testid={`closing-item-${item.key}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="font-heading text-ink text-sm flex items-center gap-1.5">
            {item.label}
            {item.is_custom && (
              <span className="text-[9px] font-mono uppercase tracking-widest bg-navy/10 text-ink-muted px-1 rounded" title={`Added by ${item.added_by || "admin"}`}>
                custom
              </span>
            )}
          </div>
          <div className="text-[11px] text-ink-muted mt-0.5">{item.hint}</div>
        </div>
        <div className="flex items-start gap-1 shrink-0">
          {statusPill()}
          {isAdmin && (
            <div className="flex flex-col gap-1">
              {item.is_custom ? (
                <button
                  onClick={onRemove}
                  title="Remove custom item"
                  className="text-rose-700 hover:bg-rose-500/10 rounded p-0.5"
                  data-testid={`closing-remove-${item.key}`}
                >
                  <Trash2 size={11} />
                </button>
              ) : (
                <button
                  onClick={onHide}
                  title="Hide from this deal"
                  className="text-ink-muted hover:text-ink hover:bg-navy/5 rounded p-0.5"
                  data-testid={`closing-hide-${item.key}`}
                >
                  <EyeOff size={11} />
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Complete state */}
      {isComplete && (
        <div className="text-[10px] font-mono text-ink-muted mt-2">
          {isAuto
            ? <>Auto-verified by system{item.auto_completed_at ? ` · ${formatDateTime(item.auto_completed_at)}` : ""}</>
            : <>By {item.completed_by} · {formatDateTime(item.completed_at)}{item.completion_note ? ` · ${item.completion_note}` : ""}</>}
          {isAdmin && (
            <button onClick={onReopen} className="ml-2 text-rose-800 hover:underline inline-flex items-center gap-0.5" data-testid={`closing-reopen-${item.key}`}>
              <RotateCcw size={10} /> Re-open{isAuto ? " (override auto)" : ""}
            </button>
          )}
        </div>
      )}

      {/* Exception state */}
      {isException && (
        <div className="text-[10px] font-mono text-amber-800 bg-amber-500/10 rounded px-2 py-1 mt-2">
          <AlertTriangle size={10} className="inline mr-1" />
          Exception granted by {item.exception_by} · {formatDateTime(item.exception_at)}
          <div className="text-ink-soft normal-case mt-1">&ldquo;{item.exception_reason}&rdquo;</div>
          {isAdmin && (
            <button onClick={onReopen} className="mt-1 text-rose-800 hover:underline inline-flex items-center gap-0.5" data-testid={`closing-reopen-${item.key}`}>
              <RotateCcw size={10} /> Re-open
            </button>
          )}
        </div>
      )}

      {/* Requested — awaiting admin */}
      {isRequested && (
        <div className="mt-2 text-[11px] space-y-1">
          <div className="text-ink-muted font-mono">
            Submitted by {item.request_by_email} · {formatDateTime(item.request_at)}
          </div>
          {item.request_note && (
            <div className="text-ink-soft italic">&ldquo;{item.request_note}&rdquo;</div>
          )}
          {item.has_evidence_file && (
            <a
              href={`${API_BASE}/deal-room/${roomId}/closing-checklist/${item.key}/evidence`}
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-1 text-[#8A6600] hover:underline"
              data-testid={`closing-evidence-${item.key}`}
            >
              <Download size={11} /> Evidence: {item.request_filename}
            </a>
          )}
          {isAdmin && (
            <div className="mt-2 space-y-1">
              <input
                type="text"
                value={approveNote}
                onChange={(e) => setApproveNote(e.target.value)}
                placeholder="Optional approval note"
                className="w-full text-[11px] border border-navy/15 rounded px-2 py-1"
                data-testid={`closing-approve-note-${item.key}`}
              />
              <div className="flex gap-2">
                <button
                  onClick={submitApprove}
                  disabled={approving}
                  className="text-[11px] font-heading text-emerald-800 hover:bg-emerald-500/10 rounded px-2 py-1 border border-emerald-500/40 inline-flex items-center gap-1"
                  data-testid={`closing-approve-${item.key}`}
                >
                  <CheckCircle2 size={11} /> Approve
                </button>
                <button
                  onClick={onException}
                  className="text-[11px] font-heading text-[#8A6600] hover:bg-gold/10 rounded px-2 py-1 border border-gold/40 inline-flex items-center gap-1"
                  data-testid={`closing-exception-${item.key}`}
                >
                  <ShieldOff size={11} /> Exception
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Pending — user can request, admin can also grant exception outright */}
      {canRequest && (
        <div className="mt-2">
          {requesting !== true && (
            <div className="flex gap-2">
              <button
                onClick={() => setRequesting(item.key)}
                className="text-[11px] font-heading text-navy hover:bg-navy/10 rounded px-2 py-1 border border-navy/30 inline-flex items-center gap-1"
                data-testid={`closing-request-open-${item.key}`}
              >
                <Upload size={11} /> Submit proof
              </button>
              {isAdmin && (
                <button
                  onClick={onException}
                  className="text-[11px] font-heading text-[#8A6600] hover:bg-gold/10 rounded px-2 py-1 border border-gold/40 inline-flex items-center gap-1"
                  data-testid={`closing-exception-open-${item.key}`}
                >
                  <ShieldOff size={11} /> Grant exception
                </button>
              )}
            </div>
          )}
          {requesting === item.key && (
            <div className="mt-2 border border-navy/10 rounded p-2 bg-white/40 space-y-2">
              <div className="flex items-center justify-between">
                <div className="text-[10px] font-mono uppercase tracking-widest text-ink-muted">Submit proof</div>
                <button onClick={() => setRequesting(null)} data-testid={`closing-request-cancel-${item.key}`}>
                  <X size={12} className="text-ink-muted hover:text-ink" />
                </button>
              </div>
              <input
                type="text"
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Short note for admin (optional)"
                className="w-full text-xs border border-navy/15 rounded px-2 py-1"
                data-testid={`closing-request-note-${item.key}`}
              />
              <input
                ref={fileInputRef}
                type="file"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="w-full text-xs"
                data-testid={`closing-request-file-${item.key}`}
              />
              {item.requires_upload && (
                <div className="text-[10px] font-mono text-ink-muted">
                  File required for this item. If you can&apos;t provide one, ask an admin to grant an exception.
                </div>
              )}
              <Button
                variant="gold"
                onClick={submitRequest}
                disabled={busy}
                testId={`closing-request-submit-${item.key}`}
              >
                {busy ? "Submitting…" : "Submit for review"}
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Auto item still awaiting signal */}
      {isAuto && !isComplete && (
        <div className="mt-2 text-[10px] font-mono text-ink-muted italic">
          Waits for: {item.auto_signal === "definitive_signed" ? "Definitive Docs to be e-signed" :
                       item.auto_signal === "invoice_paid" ? "Success-fee invoice to be paid" :
                       item.auto_signal}
        </div>
      )}
    </div>
  );
}
